import React from 'react';
import { AIInsightsSheet } from '../AIAssistant/AIInsightsSheet';
import { SmartAutoFillDialog } from '../AIAssistant/SmartAutoFillDialog';
import { AuditHistoryDialog } from '../AuditHistory';
import { PreStageRuleConfirmationDialog } from '../PreStageRuleConfirmationDialog';
import { type OvertimeClassificationResult } from './OvertimeClassificationModal';
import { CopyLastWeekDialog } from '../CopyLastWeekDialog';
import { AbsenceDetailsDialog } from './dialogs/AbsenceDetailsDialog';
import { BookingDetailsDialog } from './dialogs/BookingDetailsDialog';
import { DeleteRowDialog } from './dialogs/DeleteRowDialog';
import { DismissBookingDialog } from './dialogs/DismissBookingDialog';
import { FillEmptyDescriptionsDialog } from './dialogs/FillEmptyDescriptionsDialog';
import { OvertimeClassificationDialog } from './dialogs/OvertimeClassificationDialog';
import type { AnomalyWarning, AutoFillSuggestion } from '@/services/aiService';
import type { CostCentre, DayKey, Project, Timesheet, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings, OvertimeType } from '@/types/timesheetSettings';

export interface TimesheetGridDialogsProps {
  // Fill Empty Descriptions dialog
  isDaily: boolean;
  showFillDescriptionDialog: boolean;
  setShowFillDescriptionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  emptyDescriptionCount: number;
  bulkDescription: string;
  setBulkDescription: React.Dispatch<React.SetStateAction<string>>;
  handleFillEmptyDescriptions: () => void;

  // Booking Details / Dismiss modals
  selectedBookingForDetails: TimesheetEntry | null;
  setSelectedBookingForDetails: React.Dispatch<React.SetStateAction<TimesheetEntry | null>>;
  selectedBookingForDismiss: TimesheetEntry | null;
  setSelectedBookingForDismiss: React.Dispatch<React.SetStateAction<TimesheetEntry | null>>;
  handleDismissBooking: (index: number, reason: string, customReason?: string) => void;

  // Absence Details modal
  selectedAbsenceForDetails: TimesheetEntry | null;
  setSelectedAbsenceForDetails: React.Dispatch<React.SetStateAction<TimesheetEntry | null>>;

  // AI Insights sheet
  showAIInsightsSheet: boolean;
  setShowAIInsightsSheet: React.Dispatch<React.SetStateAction<boolean>>;
  aiWarnings: AnomalyWarning[];
  handleDismissWarning: (warningId: string) => void;
  handleAIClassifyOvertime: (suggestedOvertimeHours: Partial<Record<string, number>>) => void;
  nonDismissableWarningIds: Set<string>;

  // Smart Auto-Fill dialog
  showAutoFillDialog: boolean;
  setShowAutoFillDialog: React.Dispatch<React.SetStateAction<boolean>>;
  autoFillSuggestions: AutoFillSuggestion[];
  projects: Project[];
  handleApplyAutoFill: (suggestions: AutoFillSuggestion[]) => void;

  // Overtime Classification modal
  classifyOvertimeEntry: TimesheetEntry | null;
  setClassifyOvertimeEntry: React.Dispatch<React.SetStateAction<TimesheetEntry | null>>;
  classifyOvertimeSuggestedHours: Partial<Record<DayKey, number>>;
  setClassifyOvertimeSuggestedHours: React.Dispatch<React.SetStateAction<Partial<Record<DayKey, number>>>>;
  visibleDays: DayKey[];
  dayLabels: string[];
  activeOvertimeTypes: OvertimeType[];
  defaultOvertimeTypeId: number | undefined;
  handleClassifyOvertimeConfirm: (result: OvertimeClassificationResult) => void;
  handleProjectSearch: (query: string) => void;
  isSearchingProjects: boolean;

  // Copy Last Week dialog
  showCopyLastWeekDialog: boolean;
  setShowCopyLastWeekDialog: React.Dispatch<React.SetStateAction<boolean>>;
  copyLastWeekSourceEntries: TimesheetEntry[];
  handleCopyLastWeekConfirm: (selectedEntries: TimesheetEntry[]) => void;

  // Pre-stage rule confirmation dialog
  confirmationDialogOpen: boolean;
  confirmationMessage: string;
  isSubmitting: boolean;
  handleConfirmationAccept: () => void;
  handleConfirmationDecline: () => void;

  // Delete row confirmation
  deleteConfirmationOpen: boolean;
  onDeleteConfirmationChange: (open: boolean) => void;
  handleConfirmDelete: () => void;

  // Audit history dialog
  showAuditHistory: boolean;
  setShowAuditHistory: React.Dispatch<React.SetStateAction<boolean>>;
  timesheetId: number;
  employeeName: string;
  weekEnding: Date;
  timesheet: Timesheet;

  // Shared context
  allAvailableProjects: Project[];
  costCentres: CostCentre[];
  entries: TimesheetEntry[];
  isReadOnly: boolean;
  overtimeEnabled: boolean;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  companyId: string | undefined;
}

/** Every modal, sheet and confirmation rendered alongside the timesheet grid. */
export function TimesheetGridDialogs({
  isDaily,
  showFillDescriptionDialog,
  setShowFillDescriptionDialog,
  emptyDescriptionCount,
  bulkDescription,
  setBulkDescription,
  handleFillEmptyDescriptions,
  selectedBookingForDetails,
  setSelectedBookingForDetails,
  selectedBookingForDismiss,
  setSelectedBookingForDismiss,
  handleDismissBooking,
  selectedAbsenceForDetails,
  setSelectedAbsenceForDetails,
  showAIInsightsSheet,
  setShowAIInsightsSheet,
  aiWarnings,
  handleDismissWarning,
  handleAIClassifyOvertime,
  nonDismissableWarningIds,
  showAutoFillDialog,
  setShowAutoFillDialog,
  autoFillSuggestions,
  projects,
  handleApplyAutoFill,
  classifyOvertimeEntry,
  setClassifyOvertimeEntry,
  classifyOvertimeSuggestedHours,
  setClassifyOvertimeSuggestedHours,
  visibleDays,
  dayLabels,
  activeOvertimeTypes,
  defaultOvertimeTypeId,
  handleClassifyOvertimeConfirm,
  handleProjectSearch,
  isSearchingProjects,
  showCopyLastWeekDialog,
  setShowCopyLastWeekDialog,
  copyLastWeekSourceEntries,
  handleCopyLastWeekConfirm,
  confirmationDialogOpen,
  confirmationMessage,
  isSubmitting,
  handleConfirmationAccept,
  handleConfirmationDecline,
  deleteConfirmationOpen,
  onDeleteConfirmationChange,
  handleConfirmDelete,
  showAuditHistory,
  setShowAuditHistory,
  timesheetId,
  employeeName,
  weekEnding,
  timesheet,
  allAvailableProjects,
  costCentres,
  entries,
  isReadOnly,
  overtimeEnabled,
  organizationSettings,
  companyId,
}: TimesheetGridDialogsProps) {
  return (
    <>
      {/* Fill Empty Descriptions Dialog — hidden in daily mode */}
      {!isDaily && (
        <FillEmptyDescriptionsDialog
          open={showFillDescriptionDialog}
          onOpenChange={setShowFillDescriptionDialog}
          emptyDescriptionCount={emptyDescriptionCount}
          bulkDescription={bulkDescription}
          setBulkDescription={setBulkDescription}
          onFill={handleFillEmptyDescriptions}
        />
      )}

      <BookingDetailsDialog
        selectedBookingForDetails={selectedBookingForDetails}
        setSelectedBookingForDetails={setSelectedBookingForDetails}
        setSelectedBookingForDismiss={setSelectedBookingForDismiss}
        allAvailableProjects={allAvailableProjects}
        costCentres={costCentres}
        entries={entries}
        isReadOnly={isReadOnly}
      />

      <DismissBookingDialog
        selectedBookingForDismiss={selectedBookingForDismiss}
        setSelectedBookingForDismiss={setSelectedBookingForDismiss}
        handleDismissBooking={handleDismissBooking}
        allAvailableProjects={allAvailableProjects}
        entries={entries}
      />

      <AbsenceDetailsDialog
        selectedAbsenceForDetails={selectedAbsenceForDetails}
        setSelectedAbsenceForDetails={setSelectedAbsenceForDetails}
      />

      {/* AI Insights Sheet */}
      <AIInsightsSheet
        open={showAIInsightsSheet}
        onOpenChange={setShowAIInsightsSheet}
        warnings={aiWarnings}
        onDismiss={handleDismissWarning}
        onAutoFix={undefined}
        onClassifyOvertime={overtimeEnabled && !isReadOnly ? handleAIClassifyOvertime : undefined}
        nonDismissableIds={nonDismissableWarningIds}
      />

      {/* Smart Auto-Fill Dialog */}
      <SmartAutoFillDialog
        open={showAutoFillDialog}
        onOpenChange={setShowAutoFillDialog}
        suggestions={autoFillSuggestions}
        projects={projects}
        onApply={handleApplyAutoFill}
      />

      <OvertimeClassificationDialog
        classifyOvertimeEntry={classifyOvertimeEntry}
        setClassifyOvertimeEntry={setClassifyOvertimeEntry}
        classifyOvertimeSuggestedHours={classifyOvertimeSuggestedHours}
        setClassifyOvertimeSuggestedHours={setClassifyOvertimeSuggestedHours}
        visibleDays={visibleDays}
        dayLabels={dayLabels}
        activeOvertimeTypes={activeOvertimeTypes}
        defaultOvertimeTypeId={defaultOvertimeTypeId}
        handleClassifyOvertimeConfirm={handleClassifyOvertimeConfirm}
        handleProjectSearch={handleProjectSearch}
        isSearchingProjects={isSearchingProjects}
        allAvailableProjects={allAvailableProjects}
        costCentres={costCentres}
        overtimeEnabled={overtimeEnabled}
        companyId={companyId}
      />

      {/* Copy Last Week — select which previous-week entries to copy */}
      <CopyLastWeekDialog
        open={showCopyLastWeekDialog}
        onOpenChange={setShowCopyLastWeekDialog}
        sourceEntries={copyLastWeekSourceEntries}
        projects={allAvailableProjects}
        costCentres={costCentres}
        defaultBillableOnly={organizationSettings?.deadline?.copyOnlyBillable ?? false}
        onConfirm={handleCopyLastWeekConfirm}
      />

      {/* Pre-stage rule confirmation dialog (Accept / Decline) */}
      <PreStageRuleConfirmationDialog
        open={confirmationDialogOpen}
        message={confirmationMessage}
        isSubmitting={isSubmitting}
        onAccept={handleConfirmationAccept}
        onDecline={handleConfirmationDecline}
      />

      {/* Audit History Dialog */}
      <AuditHistoryDialog
        open={showAuditHistory}
        onOpenChange={setShowAuditHistory}
        timesheetId={timesheetId}
        employeeName={employeeName}
        weekEnding={weekEnding}
        timesheet={timesheet}
      />

      <DeleteRowDialog
        open={deleteConfirmationOpen}
        onOpenChange={onDeleteConfirmationChange}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
